import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { useCreateCampaign } from "@/hooks/useApi";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Users,
  HelpCircle,
  DollarSign,
  Settings,
  CheckCircle,
  Plus,
  Trash2,
  Eye,
  Lock,
  LockOpen,
  Rocket,
  Target,
  TrendingUp,
  Shield,
  Image,
} from "lucide-react";

// Categories for campaigns
const categories = [
  "Productivity",
  "Food & Drink",
  "Finance",
  "Health",
  "Entertainment",
  "Education",
  "Social",
  "Travel",
  "Technology",
  "Business",
  "Lifestyle",
  "Other",
];

// Available banner images - category-specific
const bannerImages = [
  // Productivity
  {
    id: "productivity-workspace",
    name: "Modern Workspace",
    category: "Productivity",
    url: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "productivity-desk",
    name: "Organized Desk",
    category: "Productivity",
    url: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=200&h=100",
  },

  // Food & Drink
  {
    id: "food-culinary",
    name: "Culinary Excellence",
    category: "Food & Drink",
    url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "food-restaurant",
    name: "Restaurant Ambiance",
    category: "Food & Drink",
    url: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=200&h=100",
  },

  // Finance
  {
    id: "finance-charts",
    name: "Financial Analytics",
    category: "Finance",
    url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "finance-banking",
    name: "Modern Banking",
    category: "Finance",
    url: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=200&h=100",
  },

  // Health
  {
    id: "health-medical",
    name: "Medical Technology",
    category: "Health",
    url: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "health-wellness",
    name: "Wellness & Fitness",
    category: "Health",
    url: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=200&h=100",
  },

  // Entertainment
  {
    id: "entertainment-gaming",
    name: "Gaming & Entertainment",
    category: "Entertainment",
    url: "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "entertainment-music",
    name: "Music & Media",
    category: "Entertainment",
    url: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=200&h=100",
  },

  // Education
  {
    id: "education-learning",
    name: "Learning Environment",
    category: "Education",
    url: "https://images.unsplash.com/photo-1523240798132-8757214e76ba?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1523240798132-8757214e76ba?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "education-online",
    name: "Online Education",
    category: "Education",
    url: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=200&h=100",
  },

  // Social
  {
    id: "social-community",
    name: "Community Connection",
    category: "Social",
    url: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "social-networking",
    name: "Social Networking",
    category: "Social",
    url: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=200&h=100",
  },

  // Travel
  {
    id: "travel-destinations",
    name: "Travel Destinations",
    category: "Travel",
    url: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "travel-adventure",
    name: "Adventure & Exploration",
    category: "Travel",
    url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=200&h=100",
  },

  // Technology
  {
    id: "tech-innovation",
    name: "Tech Innovation",
    category: "Technology",
    url: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "tech-coding",
    name: "Coding & Development",
    category: "Technology",
    url: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=200&h=100",
  },

  // Business
  {
    id: "business-corporate",
    name: "Corporate Excellence",
    category: "Business",
    url: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "business-entrepreneur",
    name: "Entrepreneurship",
    category: "Business",
    url: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=200&h=100",
  },

  // Lifestyle
  {
    id: "lifestyle-modern",
    name: "Modern Lifestyle",
    category: "Lifestyle",
    url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "lifestyle-wellness",
    name: "Wellness Living",
    category: "Lifestyle",
    url: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=200&h=100",
  },

  // Other (Generic/Versatile)
  {
    id: "other-abstract",
    name: "Abstract Innovation",
    category: "Other",
    url: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=200&h=100",
  },
  {
    id: "other-creative",
    name: "Creative Vision",
    category: "Other",
    url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&h=250",
    thumbnail:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&h=100",
  },
];

const steps = [
  {
    id: 1,
    title: "Concept",
    description: "Define your innovation",
    icon: Sparkles,
  },
  {
    id: 2,
    title: "Validation",
    description: "Design your research",
    icon: Target,
  },
  {
    id: 3,
    title: "Monetization",
    description: "Pricing strategy",
    icon: DollarSign,
  },
  {
    id: 4,
    title: "Configuration",
    description: "Campaign settings",
    icon: Settings,
  },
  {
    id: 5,
    title: "Launch",
    description: "Start your campaign",
    icon: Rocket,
  },
];

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const createCampaignMutation = useCreateCampaign();
  const [formData, setFormData] = useState({
    title: "",
    tagline: "",
    description: "",
    problemStatement: "",
    solution: "",
    category: "",
    questions: [
      {
        text: "",
        type: "text",
        options: [] as string[],
        ratingScale: "5" as string | undefined,
        ratingLabels: "numbers" as string | undefined,
        customLabels: "" as string | undefined,
      },
    ],
    pricingOptions: [{ price: "", description: "" }],
    isPrivate: false,
    bannerImage: bannerImages[0].id,
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    // Transform form data to match backend DTO
    const campaignData = {
      title: formData.title,
      tagline: formData.tagline,
      description: formData.description,
      problemStatement: formData.problemStatement,
      solution: formData.solution,
      category: formData.category,
      bannerImage: bannerImages.find((img) => img.id === formData.bannerImage)
        ?.url,
      isPrivate: formData.isPrivate,
      status: "DRAFT" as const, // Default status for new campaigns
      questions: formData.questions
        .filter((q) => q.text.trim() && q.type)
        .map((q, index) => {
          const baseQuestion = {
            text: q.text,
            type: q.type.toUpperCase() as
              | "TEXT"
              | "EMOJI"
              | "MULTIPLE"
              | "RATING",
            order: index + 1,
          };

          // Add options for multiple choice questions
          if (q.type === "multiple" && q.options && q.options.length >= 2) {
            return {
              ...baseQuestion,
              options: q.options.filter((opt) => opt.trim()),
            };
          }

          // Add rating configuration for rating questions
          if (q.type === "rating") {
            const ratingConfig: {
              text: string;
              type: "TEXT" | "EMOJI" | "MULTIPLE" | "RATING";
              order: number;
              ratingScale?: number;
              ratingLabels?: string;
              customLabels?: string[];
            } = { ...baseQuestion };

            if (q.ratingScale) {
              ratingConfig.ratingScale = parseInt(q.ratingScale);
            }

            if (q.ratingLabels === "custom" && q.customLabels) {
              ratingConfig.customLabels = q.customLabels
                .split(",")
                .map((label) => label.trim());
            } else if (q.ratingLabels) {
              ratingConfig.ratingLabels = q.ratingLabels;
            }

            return ratingConfig;
          }

          return baseQuestion;
        }),
      pricingOptions: formData.pricingOptions
        .filter((po) => po.price.trim() && po.description.trim())
        .map((po, index) => ({
          price: po.price,
          description: po.description,
          order: index + 1,
        })),
    };

    createCampaignMutation.mutate(campaignData, {
      onSuccess: (newCampaign) => {
        // Navigate to the new campaign
        navigate(`/campaign/${newCampaign.id}`);
      },
    });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep formData={formData} onChange={handleInputChange} />
        );
      case 2:
        return <QuestionsStep formData={formData} setFormData={setFormData} />;
      case 3:
        return <PricingStep formData={formData} setFormData={setFormData} />;
      case 4:
        return (
          <SettingsStep formData={formData} onChange={handleInputChange} />
        );
      case 5:
        return <ReviewStep formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <NavBar />

      <div className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </motion.div>

          {/* Enhanced Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <TrendingUp className="w-4 h-4" />
              Product Validation Platform
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Validate Your Innovation
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Transform your product idea into validated insights. Launch a
              professional validation campaign and gather data-driven feedback
              from your target market in minutes.
            </p>

            {/* Trust indicators */}
            <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-3 sm:gap-6 mt-6 sm:mt-8 text-xs sm:text-sm text-slate-500 px-4 sm:px-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                <span>Enterprise-grade security</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                <span>1,000+ validated ideas</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                <span>Data-driven insights</span>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Progress Steps */}
          <motion.div
            className="mb-8 sm:mb-12 px-2 sm:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex justify-between items-center relative">
              {/* Progress line */}
              <div className="absolute top-1/3 left-0 w-full h-0.5 bg-slate-200 -translate-y-1/2 -z-20" />
              <div
                className="absolute -z-10 top-1/3 left-0 h-0.5 bg-gradient-to-r from-primary to-accent -translate-y-1/2 transition-all duration-500 ease-out"
                style={{
                  width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                }}
              />

              {steps.map((step) => (
                <div key={step.id} className="flex flex-col items-center">
                  <motion.div
                    className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all ${
                      currentStep >= step.id
                        ? "bg-primary border-transparent text-white shadow-lg"
                        : "bg-white border-slate-200 text-slate-400"
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    {currentStep > step.id ? (
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    ) : (
                      <step.icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    )}
                  </motion.div>
                  <div className="mt-1 sm:mt-2 text-center">
                    <p
                      className={`text-xs sm:text-sm font-medium ${
                        currentStep >= step.id
                          ? "text-slate-900"
                          : "text-slate-500"
                      }`}
                    >
                      {step.title}
                    </p>
                    <p className="text-xs text-slate-400 hidden sm:block">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          {/* Enhanced Navigation */}
          <motion.div
            className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 mt-6 sm:mt-8 px-4 sm:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="gap-2 w-full sm:w-auto text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="text-xs sm:text-sm text-slate-500 order-first sm:order-none">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < steps.length ? (
              <Button
                onClick={nextStep}
                className="gap-2 w-full sm:w-auto text-sm sm:text-base"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={createCampaignMutation.isPending}
                className="gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto text-sm sm:text-base"
              >
                {createCampaignMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span className="hidden sm:inline">Launching...</span>
                    <span className="sm:hidden">Launching</span>
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4" />
                    <span className="hidden sm:inline">Launch Campaign</span>
                    <span className="sm:hidden">Launch</span>
                  </>
                )}
              </Button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Step Components
interface StepProps {
  formData: {
    title: string;
    tagline: string;
    description: string;
    problemStatement: string;
    solution: string;
    category: string;
    questions: Array<{
      text: string;
      type: string;
      options: string[];
      ratingScale?: string;
      ratingLabels?: string;
      customLabels?: string;
    }>;
    pricingOptions: Array<{ price: string; description: string }>;
    isPrivate: boolean;
    bannerImage: string;
  };
  onChange?: (field: string, value: string | boolean) => void;
  setFormData?: React.Dispatch<React.SetStateAction<StepProps["formData"]>>;
}

const BasicInfoStep = ({ formData, onChange }: StepProps) => (
  <Card className="border-slate-200/60 shadow-lg">
    <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
      <CardTitle className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Sparkles className="w-6 h-6 text-primary" />
        </div>
        Define Your Innovation
      </CardTitle>
      <p className="text-slate-600 mt-2">
        Start by clearly articulating your product concept. A well-defined
        innovation increases engagement and response rates by up to 40%.
      </p>
    </CardHeader>
    <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="space-y-2">
          <Label htmlFor="title" className="text-sm font-medium text-slate-700">
            Product Name *
          </Label>
          <Input
            id="title"
            placeholder="e.g., TaskFlow AI, SmartHome Hub"
            value={formData.title}
            onChange={(e) => onChange("title", e.target.value)}
            className="border-slate-200 text-sm sm:text-base"
          />
          <p className="text-xs text-slate-500">
            Choose a memorable, descriptive name that reflects your solution
          </p>
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="tagline"
            className="text-sm font-medium text-slate-700"
          >
            Value Proposition *
          </Label>
          <Input
            id="tagline"
            placeholder="The one-line benefit that hooks your audience"
            value={formData.tagline}
            onChange={(e) => onChange("tagline", e.target.value)}
            className="border-slate-200 text-sm sm:text-base"
          />
          <p className="text-xs text-slate-500">
            Focus on the primary benefit users will experience
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="description"
          className="text-sm font-medium text-slate-700"
        >
          Product Overview *
        </Label>
        <Textarea
          id="description"
          placeholder="Describe your product's core functionality, key features, and how it works..."
          className="min-h-24 border-slate-200 text-sm sm:text-base"
          value={formData.description}
          onChange={(e) => onChange("description", e.target.value)}
        />
        <p className="text-xs text-slate-500">
          Be specific about what your product does and how it delivers value
        </p>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor="category"
          className="text-sm font-medium text-slate-700"
        >
          Category *
        </Label>
        <select
          id="category"
          className="w-full p-3 border border-slate-200 rounded-lg bg-white text-sm sm:text-base"
          value={formData.category}
          onChange={(e) => onChange("category", e.target.value)}
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-500">
          Choose the category that best describes your product
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="space-y-2">
          <Label
            htmlFor="problemStatement"
            className="text-sm font-medium text-slate-700"
          >
            Market Problem
          </Label>
          <Textarea
            id="problemStatement"
            placeholder="What specific pain point or challenge does your product address?"
            className="min-h-20 border-slate-200 text-sm sm:text-base"
            value={formData.problemStatement}
            onChange={(e) => onChange("problemStatement", e.target.value)}
          />
          <p className="text-xs text-slate-500">
            Help respondents understand the problem you're solving
          </p>
        </div>
        <div className="space-y-2">
          <Label
            htmlFor="solution"
            className="text-sm font-medium text-slate-700"
          >
            Your Solution
          </Label>
          <Textarea
            id="solution"
            placeholder="How does your product uniquely solve this problem?"
            className="min-h-20 border-slate-200 text-sm sm:text-base"
            value={formData.solution}
            onChange={(e) => onChange("solution", e.target.value)}
          />
          <p className="text-xs text-slate-500">
            Explain your unique approach and competitive advantages
          </p>
        </div>
      </div>

      {/* Success indicator */}
      <div className="p-3 sm:p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="flex items-start gap-2 sm:gap-3">
          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-emerald-900 text-sm sm:text-base">
              Pro Tip
            </h4>
            <p className="text-xs sm:text-sm text-emerald-700">
              Campaigns with clear problem-solution statements receive 35% more
              detailed feedback and higher engagement rates.
            </p>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const QuestionsStep = ({ formData, setFormData }: StepProps) => {
  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [
        ...formData.questions,
        {
          text: "",
          type: "text",
          options: [],
          ratingScale: "5",
          ratingLabels: "numbers",
          customLabels: "",
        },
      ],
    });
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions.splice(index, 1);
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const updateQuestion = (
    index: number,
    field: string,
    value: string | string[]
  ) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const addOption = (questionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    if (!updatedQuestions[questionIndex].options) {
      updatedQuestions[questionIndex].options = [];
    }
    updatedQuestions[questionIndex].options!.push("");
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options!.splice(optionIndex, 1);
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const updateOption = (
    questionIndex: number,
    optionIndex: number,
    value: string
  ) => {
    const updatedQuestions = [...formData.questions];
    updatedQuestions[questionIndex].options![optionIndex] = value;
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const renderQuestionTypeUI = (
    question: {
      text: string;
      type: string;
      options: string[];
      ratingScale?: string;
      ratingLabels?: string;
      customLabels?: string;
    },
    index: number
  ) => {
    switch (question.type) {
      case "multiple":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-slate-700">
                Multiple Choice Options
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addOption(index)}
                className="text-primary hover:text-primary-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Option
              </Button>
            </div>

            <div className="space-y-2">
              {question.options?.map((option: string, optionIndex: number) => (
                <div key={optionIndex} className="flex items-center gap-2">
                  <Input
                    placeholder={`Option ${optionIndex + 1}`}
                    value={option}
                    onChange={(e) =>
                      updateOption(index, optionIndex, e.target.value)
                    }
                    className="flex-1 border-slate-200"
                  />
                  {question.options.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(index, optionIndex)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {(!question.options || question.options.length < 2) && (
              <p className="text-xs text-amber-600">
                Multiple choice questions require at least 2 options
              </p>
            )}
          </div>
        );

      case "emoji":
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium text-slate-700">
              Emoji Options (Optional - defaults will be used if empty)
            </Label>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              {[
                { emoji: "😍", label: "Love it!", default: true },
                { emoji: "🙂", label: "Like it", default: true },
                { emoji: "😐", label: "Neutral", default: true },
                { emoji: "😕", label: "Unsure", default: true },
                { emoji: "👎", label: "Not for me", default: true },
              ].map((option, optionIndex) => (
                <div
                  key={optionIndex}
                  className="flex items-center gap-2 p-2 sm:p-3 border border-slate-200 rounded-lg bg-slate-50"
                >
                  <span className="text-xl sm:text-2xl">{option.emoji}</span>
                  <span className="text-xs sm:text-sm font-medium text-slate-700">
                    {option.label}
                  </span>
                  {option.default && (
                    <span className="text-xs text-slate-500 ml-auto">
                      Default
                    </span>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-500">
              These are the default emoji options. You can customize them by
              adding custom options below.
            </p>
          </div>
        );

      case "rating":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Rating Scale
                </Label>
                <select
                  className="w-full p-3 border border-slate-200 rounded-lg bg-white text-sm sm:text-base"
                  value={question.ratingScale || "5"}
                  onChange={(e) =>
                    updateQuestion(index, "ratingScale", e.target.value)
                  }
                >
                  <option value="3">1-3 Scale</option>
                  <option value="5">1-5 Scale</option>
                  <option value="7">1-7 Scale</option>
                  <option value="10">1-10 Scale</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Rating Labels
                </Label>
                <select
                  className="w-full p-3 border border-slate-200 rounded-lg bg-white text-sm sm:text-base"
                  value={question.ratingLabels || "numbers"}
                  onChange={(e) =>
                    updateQuestion(index, "ratingLabels", e.target.value)
                  }
                >
                  <option value="numbers">Numbers (1, 2, 3...)</option>
                  <option value="stars">Stars (⭐)</option>
                  <option value="custom">Custom Labels</option>
                </select>
              </div>
            </div>

            {question.ratingLabels === "custom" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Custom Labels (comma-separated)
                </Label>
                <Input
                  placeholder="e.g., Poor, Fair, Good, Very Good, Excellent"
                  value={question.customLabels || ""}
                  onChange={(e) =>
                    updateQuestion(index, "customLabels", e.target.value)
                  }
                  className="border-slate-200 text-sm sm:text-base"
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="border-slate-200/60 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
        <CardTitle className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Target className="w-6 h-6 text-primary" />
          </div>
          Design Your Research Questions
        </CardTitle>
        <p className="text-slate-600 mt-2">
          Craft strategic questions that will provide actionable insights.
          Well-designed questions can increase response quality by up to 60%.
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="space-y-4">
          {formData.questions.map((question, index: number) => (
            <motion.div
              key={index}
              className="p-4 sm:p-6 bg-slate-50/50 border border-slate-200/60 rounded-xl space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              layout
            >
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-slate-900 text-sm sm:text-base">
                  Research Question {index + 1}
                </h4>
                {formData.questions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeQuestion(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Question Text
                </Label>
                <Input
                  placeholder="What specific insight do you want to gather?"
                  value={question.text}
                  onChange={(e) =>
                    updateQuestion(index, "text", e.target.value)
                  }
                  className="border-slate-200 text-sm sm:text-base"
                />
                <p className="text-xs text-slate-500">
                  Be specific and actionable - avoid yes/no questions
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Response Format
                </Label>
                <select
                  className="w-full p-3 border border-slate-200 rounded-lg bg-white"
                  value={question.type}
                  onChange={(e) =>
                    updateQuestion(index, "type", e.target.value)
                  }
                >
                  <option value="text">Open-ended feedback</option>
                  <option value="emoji">Emoji sentiment rating</option>
                  <option value="multiple">Multiple choice selection</option>
                  <option value="rating">Numerical rating scale</option>
                </select>
                <p className="text-xs text-slate-500">
                  Choose the format that will give you the most valuable
                  insights
                </p>
              </div>

              {/* Dynamic UI based on question type */}
              {question.type && question.type !== "text" && (
                <div className="pt-4 border-t border-slate-200">
                  {renderQuestionTypeUI(question, index)}
                </div>
              )}

              {/* Question validation */}
              {question.text && question.type && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-emerald-700">
                      <span className="font-medium">
                        Question {index + 1} is ready!
                      </span>
                      {question.type === "multiple" &&
                        question.options &&
                        question.options.length >= 2 && (
                          <span>
                            {" "}
                            - {question.options.length} options configured
                          </span>
                        )}
                      {question.type === "rating" && (
                        <span>
                          {" "}
                          - {question.ratingScale || 5}-point scale configured
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={addQuestion}
          className="w-full gap-2 border-dashed border-2 py-6 sm:py-8 text-slate-600 hover:text-slate-900 hover:border-slate-300 text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          Add Research Question
        </Button>

        {/* Best practices */}
        <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2 sm:gap-3">
            <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-900 text-sm sm:text-base">
                Research Best Practices
              </h4>
              <ul className="text-xs sm:text-sm text-blue-700 mt-2 space-y-1">
                <li>• Ask about specific behaviors and preferences</li>
                <li>• Include questions about willingness to pay</li>
                <li>• Gather feedback on key features and benefits</li>
                <li>• Limit to 3-5 questions for optimal completion rates</li>
                <li>
                  • Use multiple choice for quick responses, text for detailed
                  insights
                </li>
                <li>
                  • Emoji ratings work great for sentiment and first impressions
                </li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PricingStep = ({ formData, setFormData }: StepProps) => {
  const addPricingOption = () => {
    setFormData({
      ...formData,
      pricingOptions: [
        ...formData.pricingOptions,
        { price: "", description: "" },
      ],
    });
  };

  const removePricingOption = (index: number) => {
    const updatedOptions = [...formData.pricingOptions];
    updatedOptions.splice(index, 1);
    setFormData({ ...formData, pricingOptions: updatedOptions });
  };

  const updatePricingOption = (index: number, field: string, value: string) => {
    const updatedOptions = [...formData.pricingOptions];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    setFormData({ ...formData, pricingOptions: updatedOptions });
  };

  return (
    <Card className="border-slate-200/60 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
        <CardTitle className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <DollarSign className="w-6 h-6 text-primary" />
          </div>
          Define Your Pricing Strategy
        </CardTitle>
        <p className="text-slate-600 mt-2">
          Test different price points to understand your market's willingness to
          pay. This data is crucial for your go-to-market strategy.
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="space-y-4">
          {formData.pricingOptions.map((option, index: number) => (
            <motion.div
              key={index}
              className="p-4 sm:p-6 bg-slate-50/50 border border-slate-200/60 rounded-xl space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              layout
            >
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-slate-900 text-sm sm:text-base">
                  Pricing Tier {index + 1}
                </h4>
                {formData.pricingOptions.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePricingOption(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Price Point
                  </Label>
                  <Input
                    placeholder="e.g., $9.99, $19.99, $49.99"
                    value={option.price}
                    onChange={(e) =>
                      updatePricingOption(index, "price", e.target.value)
                    }
                    className="border-slate-200 text-sm sm:text-base"
                  />
                  <p className="text-xs text-slate-500">
                    Test realistic price points for your market
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    What's Included
                  </Label>
                  <Input
                    placeholder="e.g., Basic features, Pro features, Enterprise features"
                    value={option.description}
                    onChange={(e) =>
                      updatePricingOption(index, "description", e.target.value)
                    }
                    className="border-slate-200 text-sm sm:text-base"
                  />
                  <p className="text-xs text-slate-500">
                    Describe the value proposition at this price
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={addPricingOption}
          className="w-full gap-2 border-dashed border-2 py-6 sm:py-8 text-slate-600 hover:text-slate-900 hover:border-slate-300 text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          Add Pricing Tier
        </Button>

        {/* Pricing insights */}
        <div className="p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-2 sm:gap-3">
            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-purple-900 text-sm sm:text-base">
                Pricing Validation Insights
              </h4>
              <ul className="text-xs sm:text-sm text-purple-700 mt-2 space-y-1">
                <li>• Test 2-3 price points to find the sweet spot</li>
                <li>• Include a "free" option to understand demand</li>
                <li>• Ask about feature preferences at each price level</li>
                <li>• This data will inform your final pricing strategy</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SettingsStep = ({ formData, onChange }: StepProps) => {
  const selectedImage = bannerImages.find(
    (img) => img.id === formData.bannerImage
  );

  // Helper function to get banners for the selected category
  const getBannersForCategory = (category: string) => {
    if (!category) return bannerImages; // Show all if no category selected
    return bannerImages.filter((img) => img.category === category);
  };

  // Get relevant banners for the selected category
  const relevantBanners = getBannersForCategory(formData.category);

  return (
    <Card className="border-slate-200/60 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
        <CardTitle className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Settings className="w-6 h-6 text-primary" />
          </div>
          Configure Your Campaign
        </CardTitle>
        <p className="text-slate-600 mt-2">
          Set up your campaign visibility and visual presentation to maximize
          engagement and response rates.
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Privacy Settings */}
        <div className="space-y-4">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            Campaign Visibility
          </h3>
          <RadioGroup
            defaultValue={formData.isPrivate ? "private" : "public"}
            onValueChange={(value) =>
              onChange("isPrivate", value === "private")
            }
          >
            <div className="flex items-start space-x-3 p-3 sm:p-4 border border-slate-200 rounded-xl hover:bg-slate-50/50 transition-colors">
              <RadioGroupItem value="public" id="public" className="mt-1" />
              <div className="space-y-1">
                <Label
                  htmlFor="public"
                  className="text-sm sm:text-base font-medium flex items-center gap-2"
                >
                  <LockOpen className="w-4 h-4 text-green-600" />
                  Public Campaign
                </Label>
                <p className="text-xs sm:text-sm text-slate-600">
                  Your campaign will be discoverable in our marketplace,
                  increasing exposure and potential responses.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 sm:p-4 border border-slate-200 rounded-xl hover:bg-slate-50/50 transition-colors">
              <RadioGroupItem value="private" id="private" className="mt-1" />
              <div className="space-y-1">
                <Label
                  htmlFor="private"
                  className="text-sm sm:text-base font-medium flex items-center gap-2"
                >
                  <Lock className="w-4 h-4 text-amber-600" />
                  Private Campaign
                </Label>
                <p className="text-xs sm:text-sm text-slate-600">
                  Only accessible via direct links you share. Perfect for
                  confidential or targeted validation.
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Banner Image */}
        <div className="space-y-4">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            Campaign Visual
          </h3>

          {/* Category-specific banner selection */}
          {formData.category && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <div className="p-1 bg-blue-100 rounded">
                  <Image className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-blue-900 text-sm">
                    Category-Specific Banners
                  </h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Showing banners tailored for your "{formData.category}"
                    category.
                    {!formData.category &&
                      " Select a category above to see relevant options."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="relative w-full h-24 sm:h-32 overflow-hidden rounded-xl shadow-md">
            <img
              src={selectedImage?.url}
              alt="Selected banner"
              className="w-full h-full object-cover"
            />
            {selectedImage?.category && (
              <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                {selectedImage.category}
              </div>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-slate-700">
                {formData.category
                  ? `${formData.category} Banners`
                  : "All Banners"}
              </h4>
              {formData.category && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onChange(
                      "bannerImage",
                      relevantBanners[0]?.id || bannerImages[0].id
                    )
                  }
                  className="text-xs text-primary hover:text-primary-700"
                >
                  Auto-select first
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {relevantBanners.map((image) => (
                <motion.div
                  key={image.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onChange("bannerImage", image.id)}
                  className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all relative ${
                    formData.bannerImage === image.id
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-slate-200"
                  }`}
                >
                  <img
                    src={image.thumbnail}
                    alt={image.name}
                    className="w-full h-12 sm:h-16 object-cover"
                  />
                  {formData.bannerImage === image.id && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <CheckCircle className="text-primary w-4 h-4 sm:w-6 sm:h-6 bg-white rounded-full" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-1">
                    <p className="text-xs truncate">{image.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Campaign optimization tips */}
        <div className="p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2 sm:gap-3">
            <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-amber-900 text-sm sm:text-base">
                Campaign Optimization
              </h4>
              <ul className="text-xs sm:text-sm text-amber-700 mt-2 space-y-1">
                <li>• Public campaigns receive 3x more responses on average</li>
                <li>• Category-specific visuals increase engagement by 40%</li>
                <li>• You can change visibility settings anytime</li>
                <li>• Professional banners improve campaign credibility</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ReviewStep = ({ formData }: StepProps) => {
  const selectedBanner = bannerImages.find(
    (img) => img.id === formData.bannerImage
  );

  return (
    <Card className="border-slate-200/60 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
        <CardTitle className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Rocket className="w-6 h-6 text-primary" />
          </div>
          Launch Your Validation Campaign
        </CardTitle>
        <p className="text-slate-600 mt-2">
          Review your campaign details and launch to start collecting valuable
          market insights from your target audience.
        </p>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Campaign Preview */}
        <div className="relative overflow-hidden rounded-xl h-32 sm:h-40">
          <img
            src={selectedBanner?.url}
            alt="Campaign banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
              {formData.title || "Your Product Name"}
            </h2>
            <p className="text-white/80 text-sm sm:text-base">
              {formData.tagline || "Your value proposition"}
            </p>
          </div>
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
            {formData.isPrivate ? (
              <div className="flex items-center bg-amber-500/80 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm gap-1">
                <Lock className="w-3 h-3 sm:w-4 sm:h-4" /> Private
              </div>
            ) : (
              <div className="flex items-center bg-green-500/80 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm gap-1">
                <LockOpen className="w-3 h-3 sm:w-4 sm:h-4" /> Public
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2 text-sm sm:text-base">
              Product Details
            </h3>
            <div className="space-y-3 text-xs sm:text-sm">
              <div>
                <span className="font-medium text-slate-600">
                  Product Name:
                </span>
                <p className="text-slate-900">{formData.title || "Not set"}</p>
              </div>
              <div>
                <span className="font-medium text-slate-600">
                  Value Proposition:
                </span>
                <p className="text-slate-900 line-clamp-3">
                  {formData.tagline || "Not set"}
                </p>
              </div>
              <div>
                <span className="font-medium text-slate-600">
                  Product Overview:
                </span>
                <p className="text-slate-900 line-clamp-3">
                  {formData.description || "Not set"}
                </p>
              </div>
              <div>
                <span className="font-medium text-slate-600">Category:</span>
                <p className="text-slate-900">
                  {formData.category || "Not set"}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2 text-sm sm:text-base">
              Validation Strategy
            </h3>
            <div className="space-y-3 text-xs sm:text-sm">
              <div>
                <span className="font-medium text-slate-600">
                  Research Questions:
                </span>
                <p className="text-slate-900">
                  {formData.questions.length} question(s)
                </p>
              </div>
              <div>
                <span className="font-medium text-slate-600">
                  Pricing Tiers:
                </span>
                <p className="text-slate-900">
                  {formData.pricingOptions.length} option(s)
                </p>
              </div>
              <div>
                <span className="font-medium text-slate-600">
                  Campaign Visibility:
                </span>
                <p className="text-slate-900">
                  {formData.isPrivate
                    ? "Private (Direct links only)"
                    : "Public (Marketplace discoverable)"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Launch Notice */}
        <div className="p-4 sm:p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
              <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">
                Ready to Launch Your Validation Campaign!
              </h4>
              <p className="text-slate-600 mb-3 text-xs sm:text-sm leading-relaxed">
                Your campaign will be live immediately and start collecting
                valuable market insights. You can edit settings, add resources,
                and view analytics from your dashboard anytime.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                  <span>Instant launch</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                  <span>Real-time analytics</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                  <span>Editable anytime</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreateCampaign;
